import React, { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import 'dayjs/locale/uz';
import {
    ChevronLeftIcon,
    ChevronRightIcon,
    MagnifyingGlassIcon,
    PlusIcon,
    DocumentTextIcon,
    UsersIcon,
} from '@heroicons/react/24/outline';
import { PencilIcon } from '@heroicons/react/24/solid';
import { CalendarOutlined } from '@ant-design/icons';
import { DatePicker, Drawer, Grid, Input, Spin } from 'antd';
import { getDoctorColor, getInitials } from './calendarUtils';
import CalendarGridView from './CalendarGridView';
import TodayByDoctorsModal from './TodayByDoctorsModal';
import TodayDoctorsMiniSidebar from './TodayDoctorsMiniSidebar';
import {
    ActionButton,
    PageCard,
    PageShell,
    PageToolbar,
    RowIconButton,
    StatusBadge,
} from '../ui';
import { useTranslation } from 'react-i18next';

dayjs.extend(isoWeek);

const { useBreakpoint } = Grid;

const UNTASSIGNED_KEY = 'unassigned';
const UNASSIGNED_COLOR = '#94a3b8';

function navUnitForView(layoutView, boardViewMode) {
    if (layoutView === 'grid') return 'month';
    if (boardViewMode === 'month') return 'month';
    if (boardViewMode === 'week') return 'week';
    return 'day';
}

function formatHeaderTitle(date, layoutView, boardViewMode) {
    if (layoutView === 'grid' || boardViewMode === 'month') {
        return date.format('MMMM YYYY');
    }
    if (boardViewMode === 'week') {
        const start = date.startOf('isoWeek');
        const end = date.endOf('isoWeek');
        if (start.year() !== end.year()) {
            return `${start.format('D MMM YYYY')} – ${end.format('D MMM YYYY')}`;
        }
        if (start.month() !== end.month()) {
            return `${start.format('D MMMM')} – ${end.format('D MMMM YYYY')}`;
        }
        return `${start.format('D')} – ${end.format('D MMMM YYYY')}`;
    }
    return date.format('dddd, D MMMM YYYY');
}

function navButtonLabel(t, layoutView, boardViewMode, direction) {
    const prev = direction < 0;
    if (layoutView === 'grid' || boardViewMode === 'month') return prev ? t('calendar.prevMonth') : t('calendar.nextMonth');
    if (boardViewMode === 'week') return prev ? t('calendar.prevWeek') : t('calendar.nextWeek');
    return prev ? t('calendar.prevDay') : t('calendar.nextDay');
}

function countEventsOnDay(events, day) {
    return events.filter((e) => dayjs(e.date).isSame(day, 'day')).length;
}

/** Shared look for the small segmented switches in the toolbar. */
function SegmentedGroup({ children }) {
    return (
        <div className="flex items-center gap-0.5 rounded-lg border border-slate-200 bg-slate-50 p-0.5">
            {children}
        </div>
    );
}

function SegmentedButton({ active, onClick, children }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`rounded-md px-2.5 py-1 text-xs font-semibold transition-colors ${
                active ? 'bg-[#1D2C7B] text-white' : 'text-slate-600 hover:bg-white'
            }`}
        >
            {children}
        </button>
    );
}

function WeekDayStrip({ anchorDate, events, onPickDate }) {
    const start = anchorDate.startOf('isoWeek');
    const days = Array.from({ length: 7 }, (_, i) => start.add(i, 'day'));

    return (
        <div className="flex gap-1.5 overflow-x-auto px-2.5 py-2 sm:px-3">
            {days.map((day) => {
                const selected = day.isSame(anchorDate, 'day');
                const count = countEventsOnDay(events, day);

                return (
                    <button
                        key={day.format('YYYY-MM-DD')}
                        type="button"
                        onClick={() => onPickDate(day)}
                        className={`flex min-w-[58px] shrink-0 flex-col items-center rounded-lg border px-2.5 py-1.5 transition-colors sm:min-w-[66px] ${
                            selected
                                ? 'border-[#1D2C7B] bg-[#1D2C7B] text-white'
                                : 'border-slate-200 bg-white text-slate-700 hover:border-[#1D2C7B]/40'
                        }`}
                    >
                        <span
                            className={`text-[10px] font-semibold uppercase ${
                                selected ? 'text-white/80' : 'text-slate-400'
                            }`}
                        >
                            {day.format('ddd')}
                        </span>
                        <span className="text-base font-semibold leading-tight tabular-nums">
                            {day.format('D')}
                        </span>
                        <span
                            className={`text-[10px] font-semibold tabular-nums ${
                                selected ? 'text-white/80' : 'text-[#1D2C7B]'
                            } ${count > 0 ? '' : 'invisible'}`}
                        >
                            {count}
                        </span>
                    </button>
                );
            })}
        </div>
    );
}

function eventStatus(event, referenceDate) {
    const d = dayjs(event.date);
    if (d.isBefore(referenceDate, 'day')) return { key: 'past', tone: 'slate' };
    if (d.isAfter(referenceDate, 'day')) return { key: 'planned', tone: 'blue' };
    return { key: 'todayLabel', tone: 'green' };
}

function AppointmentCard({ event, accent, onClick, compact }) {
    const { t } = useTranslation();
    const status = eventStatus(event, dayjs());

    return (
        <button
            type="button"
            onClick={() => onClick(event)}
            className={`w-full overflow-hidden rounded-lg border border-slate-200 bg-white text-left transition-colors hover:border-[#1D2C7B]/40 ${
                compact ? 'p-2' : 'p-2.5'
            }`}
        >
            <span className="flex gap-2">
                <span
                    className="w-1 shrink-0 self-stretch rounded-full"
                    style={{ backgroundColor: accent }}
                    aria-hidden
                />
                <span className="min-w-0 flex-1">
                    <span
                        className={`block truncate font-medium text-[#1D2C7B] ${
                            compact ? 'text-xs' : 'text-sm'
                        }`}
                    >
                        {event.title}
                    </span>
                    {event.phone && (
                        <span className="mt-0.5 block truncate text-xs text-slate-500 tabular-nums">
                            {event.phone}
                        </span>
                    )}
                    {event.description && (
                        <span className="mt-1 flex items-center gap-1 text-xs text-slate-500">
                            <DocumentTextIcon className="h-3.5 w-3.5 shrink-0" />
                            <span className="truncate">{event.description}</span>
                        </span>
                    )}
                    <span className="mt-1.5 flex">
                        <StatusBadge tone={status.tone}>{t(`calendar.${status.key}`)}</StatusBadge>
                    </span>
                </span>
            </span>
        </button>
    );
}

function DoctorIdentity({ doctor, accent, count, isUnassigned, stacked }) {
    const { t } = useTranslation();
    const name = isUnassigned ? t('calendar.unassigned') : doctor?.name ?? t('common.dash');
    const initials = isUnassigned ? '?' : getInitials(doctor?.name);

    return (
        <div className="flex items-center gap-2">
            <span
                className={`flex shrink-0 items-center justify-center rounded-full font-semibold text-white ${
                    stacked ? 'h-8 w-8 text-xs' : 'h-9 w-9 text-sm'
                }`}
                style={{ backgroundColor: accent }}
            >
                {initials}
            </span>
            <span className="min-w-0">
                <span className="block truncate text-sm font-medium text-[#1D2C7B]">{name}</span>
                <span className="block text-xs text-slate-500">
                    <span className="tabular-nums">{count}</span> {t('calendar.visits')} · {t('calendar.dentist')}
                </span>
            </span>
        </div>
    );
}

function AddForDoctorButton({ onClick }) {
    const { t } = useTranslation();
    return (
        <button
            type="button"
            onClick={onClick}
            className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-slate-200 px-2 py-1 text-xs font-semibold text-[#1D2C7B] transition-colors hover:bg-slate-50"
        >
            <PlusIcon className="h-4 w-4" />
            {t('calendar.add')}
        </button>
    );
}

function DayDoctorGrid({ date, events, doctors, colorMap, onEventClick, onAddForDoctor }) {
    const { t } = useTranslation();
    const screens = useBreakpoint();
    const isPhone = screens.md === false;
    const dayEvents = events.filter((e) => dayjs(e.date).isSame(date, 'day'));

    const columns = useMemo(() => {
        const cols = doctors.map((d) => ({ key: String(d.id), doctor: d }));
        cols.push({ key: UNTASSIGNED_KEY, doctor: null, isUnassigned: true });
        return cols;
    }, [doctors]);

    const byDoctor = (doctorKey) =>
        dayEvents.filter((e) => {
            const id = e.doctor_id ?? null;
            if (doctorKey === UNTASSIGNED_KEY) return id == null;
            return String(id) === doctorKey;
        });

    const accentFor = (col) =>
        col.isUnassigned ? UNASSIGNED_COLOR : getDoctorColor(col.doctor?.id, colorMap);

    // Phones get a single-column vertical agenda instead of a row of columns
    // that would otherwise be squeezed to a few dozen pixels each.
    if (isPhone) {
        return (
            <div className="divide-y divide-slate-100">
                {columns.map((col) => {
                    const list = byDoctor(col.key);

                    return (
                        <section key={col.key} className="px-2.5 py-2.5">
                            <div className="flex items-start justify-between gap-2">
                                <DoctorIdentity
                                    doctor={col.doctor}
                                    accent={accentFor(col)}
                                    count={list.length}
                                    isUnassigned={col.isUnassigned}
                                    stacked
                                />
                                {onAddForDoctor && (
                                <AddForDoctorButton
                                    onClick={() => onAddForDoctor(col.isUnassigned ? null : col.doctor?.id)}
                                />
                                )}
                            </div>

                            {list.length === 0 ? (
                                <p className="m-0 mt-2 text-xs text-slate-400">{t('calendar.noRecords')}</p>
                            ) : (
                                <div className="mt-2 space-y-2">
                                    {list.map((ev) => (
                                        <AppointmentCard
                                            key={ev.id}
                                            event={ev}
                                            accent={accentFor(col)}
                                            onClick={onEventClick}
                                        />
                                    ))}
                                </div>
                            )}
                        </section>
                    );
                })}
            </div>
        );
    }

    return (
        <div className="flex min-h-[420px] gap-0 overflow-x-auto">
            {columns.map((col) => {
                const list = byDoctor(col.key);
                const accent = accentFor(col);

                return (
                    <div
                        key={col.key}
                        className="flex max-h-[70vh] min-w-[260px] max-w-[340px] flex-1 flex-col border-r border-slate-100 last:border-r-0"
                    >
                        <div className="sticky top-0 z-20 border-b border-slate-200 bg-slate-50 px-3 py-2.5">
                            <DoctorIdentity
                                doctor={col.doctor}
                                accent={accent}
                                count={list.length}
                                isUnassigned={col.isUnassigned}
                            />
                        </div>

                        <div className="flex-1 space-y-2 overflow-y-auto bg-slate-50/40 p-3">
                            {onAddForDoctor && (
                            <div className="flex items-center justify-end pb-1">
                                <AddForDoctorButton
                                    onClick={() => onAddForDoctor(col.isUnassigned ? null : col.doctor?.id)}
                                />
                            </div>
                            )}
                            {list.length === 0 ? (
                                <p className="m-0 px-1 py-4 text-center text-xs text-slate-400">{t('calendar.noRecords')}</p>
                            ) : (
                                list.map((ev) => (
                                    <AppointmentCard
                                        key={ev.id}
                                        event={ev}
                                        accent={accent}
                                        onClick={onEventClick}
                                    />
                                ))
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

function WeekView({ anchorDate, events, onEventClick, onDayClick }) {
    const { t } = useTranslation();
    const screens = useBreakpoint();
    const isPhone = screens.md === false;
    const start = anchorDate.startOf('isoWeek');
    const days = Array.from({ length: 7 }, (_, i) => start.add(i, 'day'));

    if (isPhone) {
        return (
            <div className="divide-y divide-slate-100">
                {days.map((day) => {
                    const dayList = events.filter((e) => dayjs(e.date).isSame(day, 'day'));
                    const isToday = day.isSame(dayjs(), 'day');

                    return (
                        <section key={day.format()} className="px-2.5 py-2.5">
                            <button
                                type="button"
                                onClick={() => onDayClick(day)}
                                className="flex w-full items-baseline gap-2 text-left"
                            >
                                <span
                                    className={`text-base font-semibold tabular-nums ${
                                        isToday ? 'text-[#1D2C7B]' : 'text-slate-800'
                                    }`}
                                >
                                    {day.format('D')}
                                </span>
                                <span className="text-xs font-semibold uppercase text-slate-500">
                                    {day.format('ddd')}
                                </span>
                            </button>

                            {dayList.length === 0 ? (
                                <p className="m-0 mt-1.5 text-xs text-slate-400">{t('common.dash')}</p>
                            ) : (
                                <div className="mt-2 space-y-2">
                                    {dayList.map((ev) => (
                                        <AppointmentCard
                                            key={ev.id}
                                            event={ev}
                                            accent="#1D2C7B"
                                            onClick={onEventClick}
                                        />
                                    ))}
                                </div>
                            )}
                        </section>
                    );
                })}
            </div>
        );
    }

    return (
        <div className="grid grid-cols-7 gap-2 p-2.5 sm:p-3">
            {days.map((day) => {
                const dayList = events.filter((e) => dayjs(e.date).isSame(day, 'day'));
                return (
                    <div
                        key={day.format()}
                        className="min-h-[200px] rounded-xl border border-slate-200/90 bg-white p-2"
                    >
                        <button
                            type="button"
                            onClick={() => onDayClick(day)}
                            className={`mb-2 w-full border-b border-slate-100 pb-2 text-left ${
                                day.isSame(dayjs(), 'day') ? 'text-[#1D2C7B]' : 'text-slate-700'
                            }`}
                        >
                            <span className="text-xs font-semibold uppercase text-slate-500">
                                {day.format('ddd')}
                            </span>
                            <span className="block text-base font-semibold tabular-nums">
                                {day.format('D')}
                            </span>
                        </button>
                        <div className="max-h-[320px] space-y-1.5 overflow-y-auto">
                            {dayList.length === 0 ? (
                                <p className="m-0 text-xs text-slate-400">{t('common.dash')}</p>
                            ) : (
                                dayList.map((ev) => (
                                    <AppointmentCard
                                        key={ev.id}
                                        event={ev}
                                        accent="#1D2C7B"
                                        onClick={onEventClick}
                                        compact
                                    />
                                ))
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

function getStoredDoctorName() {
    try {
        const user = JSON.parse(localStorage.getItem('user') || 'null');
        if (!user) return '';
        const parts = [user.last_name, user.first_name, user.middle_name ?? user.patronymic]
            .filter(Boolean)
            .join(' ')
            .trim();
        return parts || user.name || user.full_name || '';
    } catch {
        return '';
    }
}

function DoctorWelcome({ name }) {
    const { t } = useTranslation();
    const writeDuration = name ? `${Math.min(4.6, Math.max(2, name.length * 0.11))}s` : undefined;

    return (
        <div className="doctor-welcome" title={name || undefined}>
            <span className="doctor-welcome__hello">{t('calendar.welcome')}</span>
            {name ? (
                <span className="doctor-welcome__write">
                    <span className="doctor-welcome__name" style={{ animationDuration: writeDuration }}>
                        {name}
                    </span>
                    <PencilIcon
                        className="doctor-welcome__pen"
                        style={{ animationDuration: writeDuration }}
                    />
                </span>
            ) : null}
        </div>
    );
}

export default function CalendarDashboard({
    loading,
    currentDate,
    onDateChange,
    layoutView,
    onLayoutViewChange,
    boardViewMode,
    onBoardViewModeChange,
    events,
    doctors,
    colorMap,
    searchText,
    onSearchChange,
    onEventClick,
    onAddClick,
    readOnly = false,
}) {
    const { t } = useTranslation();
    const [todayModalOpen, setTodayModalOpen] = useState(false);
    const [doctorsDrawerOpen, setDoctorsDrawerOpen] = useState(false);
    const screens = useBreakpoint();
    const isPhone = screens.md === false;
    const doctorName = useMemo(() => getStoredDoctorName(), []);

    const dayStats = useMemo(() => {
        const dayEvents = events.filter((e) => dayjs(e.date).isSame(currentDate, 'day'));
        const withDoctor = dayEvents.filter((e) => e.doctor_id != null).length;
        const withoutDoctor = dayEvents.length - withDoctor;
        return {
            total: dayEvents.length,
            withDoctor,
            withoutDoctor,
        };
    }, [events, currentDate]);

    const filteredEvents = useMemo(() => {
        if (!searchText.trim()) return events;
        const q = searchText.toLowerCase();
        return events.filter(
            (e) =>
                e.title?.toLowerCase().includes(q) ||
                e.phone?.includes(q) ||
                e.doctor?.name?.toLowerCase().includes(q)
        );
    }, [events, searchText]);

    const datePickerCellRender = useMemo(
        () => (current, info) => {
            if (info.type !== 'date') {
                return info.originNode;
            }
            const count = countEventsOnDay(filteredEvents, current);
            const isToday = current.isSame(dayjs(), 'day');
            return (
                <div className="relative mx-auto w-full">
                    <div className={isToday ? 'font-semibold text-[#1D2C7B]' : undefined}>{current.date()}</div>
                    {count > 0 && (
                        <span className="absolute -bottom-0.5 left-1/2 h-1.5 w-1.5 -translate-x-1/2 rounded-full bg-[#1D2C7B]" />
                    )}
                </div>
            );
        },
        [filteredEvents]
    );

    const goToday = () => onDateChange(dayjs());
    const unit = navUnitForView(layoutView, boardViewMode);
    const shiftDate = (delta) => onDateChange(currentDate.add(delta, unit));
    const headerTitle = formatHeaderTitle(currentDate, layoutView, boardViewMode);
    const isTodaySelected = currentDate.isSame(dayjs(), 'day');
    const isGrid = layoutView === 'grid';
    const todayDisabled = isTodaySelected && (isGrid || boardViewMode === 'day');

    const pickDate = (day) => {
        onDateChange(day);
        if (!isGrid && (boardViewMode === 'month' || boardViewMode === 'week')) {
            onBoardViewModeChange('day');
        }
    };

    const layoutLabel = isGrid
        ? t('calendar.layoutMonth')
        : boardViewMode === 'day'
          ? t('calendar.layoutDay')
          : boardViewMode === 'week'
            ? t('calendar.layoutWeek')
            : t('calendar.layoutMonthBoard');

    const doctorsPanel = (
        <TodayDoctorsMiniSidebar
            events={filteredEvents}
            doctors={doctors}
            colorMap={colorMap}
        />
    );

    return (
        <PageShell>
            <PageCard>
                <PageToolbar
                    title={
                        <span className="flex min-w-0 flex-col">
                            <span className="truncate text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                                {layoutLabel}
                            </span>
                            <span className="truncate capitalize">{headerTitle}</span>
                        </span>
                    }
                    filters={
                        <>
                            <Input
                                allowClear
                                size="small"
                                prefix={<MagnifyingGlassIcon className="h-4 w-4 text-slate-400" />}
                                placeholder={t('calendar.search')}
                                value={searchText}
                                onChange={(e) => onSearchChange(e.target.value)}
                                className="md:!w-[200px]"
                            />
                            <DatePicker
                                size="small"
                                value={currentDate}
                                onChange={(d) => d && pickDate(d)}
                                allowClear={false}
                                format="DD.MM.YYYY"
                                inputReadOnly
                                suffixIcon={<CalendarOutlined className="text-[#1D2C7B]" />}
                                placeholder={t('calendar.pickDate')}
                                className="md:!w-[140px]"
                                popupClassName="calendar-date-popup"
                                cellRender={datePickerCellRender}
                            />
                        </>
                    }
                    actions={
                        readOnly ? (
                            <DoctorWelcome name={doctorName} />
                        ) : (
                        <>
                            <RowIconButton
                                className="lg:hidden"
                                aria-label={t('calendar.todayVisits')}
                                title={t('calendar.todayVisits')}
                                onClick={() => setDoctorsDrawerOpen(true)}
                            >
                                <UsersIcon className="h-[18px] w-[18px]" />
                            </RowIconButton>
                            <ActionButton
                                icon={<PlusIcon className="h-4 w-4" />}
                                onClick={() => onAddClick(null, currentDate)}
                            >
                                {t('calendar.newVisit')}
                            </ActionButton>
                        </>
                        )
                    }
                />

                <div className="flex flex-wrap items-center gap-1.5 border-b border-slate-100 px-2.5 py-2 sm:px-3">
                    <div className="flex items-center gap-0.5 rounded-lg border border-slate-200 bg-white p-0.5">
                        <button
                            type="button"
                            title={navButtonLabel(t, layoutView, boardViewMode, -1)}
                            onClick={() => shiftDate(-1)}
                            className="rounded-md p-1.5 text-slate-500 transition-colors hover:bg-slate-100 hover:text-[#1D2C7B]"
                        >
                            <ChevronLeftIcon className="h-4 w-4" />
                        </button>
                        <button
                            type="button"
                            title={navButtonLabel(t, layoutView, boardViewMode, 1)}
                            onClick={() => shiftDate(1)}
                            className="rounded-md p-1.5 text-slate-500 transition-colors hover:bg-slate-100 hover:text-[#1D2C7B]"
                        >
                            <ChevronRightIcon className="h-4 w-4" />
                        </button>
                    </div>

                    <button
                        type="button"
                        onClick={goToday}
                        disabled={todayDisabled}
                        className={`rounded-lg border px-2.5 py-1 text-xs font-semibold transition-colors ${
                            todayDisabled
                                ? 'cursor-default border-slate-200 bg-slate-50 text-slate-400'
                                : 'border-slate-200 bg-white text-[#1D2C7B] hover:bg-slate-50'
                        }`}
                    >
                        {t('calendar.today')}
                    </button>

                    {!readOnly && (
                    <SegmentedGroup>
                        <SegmentedButton active={isGrid} onClick={() => onLayoutViewChange('grid')}>
                            {isPhone ? t('calendar.calendar') : t('calendar.layoutCalendar')}
                        </SegmentedButton>
                        <SegmentedButton active={!isGrid} onClick={() => onLayoutViewChange('board')}>
                            {isPhone ? t('calendar.doctors') : t('calendar.layoutDoctors')}
                        </SegmentedButton>
                    </SegmentedGroup>
                    )}

                    {!readOnly && !isGrid && (
                        <SegmentedGroup>
                            {['day', 'week', 'month'].map((mode) => (
                                <SegmentedButton
                                    key={mode}
                                    active={boardViewMode === mode}
                                    onClick={() => onBoardViewModeChange(mode)}
                                >
                                    {mode === 'day' ? t('calendar.day') : mode === 'week' ? t('calendar.week') : t('calendar.month')}
                                </SegmentedButton>
                            ))}
                        </SegmentedGroup>
                    )}

                    {!readOnly && isGrid && (
                        <button
                            type="button"
                            onClick={() => setTodayModalOpen(true)}
                            className="rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-[#1D2C7B] transition-colors hover:bg-slate-50"
                        >
                            {t('calendar.todayDoctors')}
                        </button>
                    )}
                </div>

                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-2.5 py-2 text-xs text-slate-500 sm:px-3">
                    <span>
                        <strong className="text-sm font-semibold text-slate-800 tabular-nums">{dayStats.total}</strong> {t('calendar.visits')}
                    </span>
                    {!readOnly && (
                        <>
                    <span className="text-slate-300">|</span>
                    <span>
                        <strong className="text-sm font-semibold text-slate-800 tabular-nums">{dayStats.withDoctor}</strong> {t('calendar.withDoctor')}
                    </span>
                    <span className="text-slate-300">|</span>
                    <span>
                        <strong className="text-sm font-semibold text-slate-800 tabular-nums">{dayStats.withoutDoctor}</strong> {t('calendar.withoutDoctor')}
                    </span>
                        </>
                    )}
                </div>

                {!isGrid && (boardViewMode === 'day' || boardViewMode === 'week') && (
                    <div className="border-t border-slate-100">
                        <WeekDayStrip
                            anchorDate={currentDate}
                            events={filteredEvents}
                            onPickDate={pickDate}
                        />
                    </div>
                )}
            </PageCard>

            <div className="flex flex-col items-start gap-2.5 lg:flex-row md:gap-4">
                {!readOnly && (
                <aside className="hidden shrink-0 lg:sticky lg:top-5 lg:block lg:h-[calc(100vh-7rem)] lg:w-[240px]">
                    {doctorsPanel}
                </aside>
                )}

                <main className="relative w-full min-w-0 flex-1">
                    {loading && (
                        <div className="absolute inset-0 z-30 flex items-center justify-center rounded-xl bg-white/70">
                            <Spin size="large" />
                        </div>
                    )}

                    <PageCard>
                        {isGrid && (
                            <CalendarGridView
                                anchorDate={currentDate}
                                events={filteredEvents}
                                colorMap={colorMap}
                                onEventClick={onEventClick}
                                onDaySelect={(d) => onDateChange(d)}
                                onAddOnDay={readOnly ? undefined : (d) => onAddClick(null, d)}
                            />
                        )}
                        {!isGrid && boardViewMode === 'day' && (
                            <DayDoctorGrid
                                date={currentDate}
                                events={filteredEvents}
                                doctors={doctors}
                                colorMap={colorMap}
                                onEventClick={onEventClick}
                                onAddForDoctor={readOnly ? undefined : (doctorId) => onAddClick(doctorId, currentDate)}
                            />
                        )}
                        {!isGrid && boardViewMode === 'week' && (
                            <WeekView
                                anchorDate={currentDate}
                                events={filteredEvents}
                                onEventClick={onEventClick}
                                onDayClick={(d) => {
                                    onDateChange(d);
                                    onBoardViewModeChange('day');
                                }}
                            />
                        )}
                        {!isGrid && boardViewMode === 'month' && (
                            <CalendarGridView
                                anchorDate={currentDate}
                                events={filteredEvents}
                                colorMap={colorMap}
                                onEventClick={onEventClick}
                                onDaySelect={(d) => {
                                    onDateChange(d);
                                    onBoardViewModeChange('day');
                                }}
                                onAddOnDay={readOnly ? undefined : (d) => onAddClick(null, d)}
                            />
                        )}
                    </PageCard>
                </main>
            </div>

            {!readOnly && (
            <Drawer
                placement="left"
                title={null}
                open={doctorsDrawerOpen}
                onClose={() => setDoctorsDrawerOpen(false)}
                width={isPhone ? '100%' : 360}
                styles={{ body: { padding: 12 } }}
            >
                {doctorsPanel}
            </Drawer>
            )}

            {!readOnly && (
            <TodayByDoctorsModal
                open={todayModalOpen}
                onClose={() => setTodayModalOpen(false)}
                events={filteredEvents}
                doctors={doctors}
                colorMap={colorMap}
                onEventClick={onEventClick}
            />
            )}
        </PageShell>
    );
}
